import { useMutation, useQuery } from '@tanstack/react-query';
import type { MutationFunction, QueryFunction, QueryKey, UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreModelsSupportErrorReport,
  BrokerNerdsBarkerCoreModelsSupportSupportRequestBase,
  BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent,
  BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing,
  BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent,
  BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing,
  FreshdeskApiClientContactsModelsContact,
} from './models';

export const getApiSupportContactsSelf = (options?: AxiosRequestConfig): Promise<AxiosResponse<FreshdeskApiClientContactsModelsContact>> => {
  return axios.get(`/api/Support/Contacts/Self`, options);
};

export const getGetApiSupportContactsSelfQueryKey = () => {
  return [`/api/Support/Contacts/Self`] as const;
};

export const getGetApiSupportContactsSelfQueryOptions = <TData = Awaited<ReturnType<typeof getApiSupportContactsSelf>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiSupportContactsSelf>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiSupportContactsSelfQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiSupportContactsSelf>>> = ({ signal }) => getApiSupportContactsSelf({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiSupportContactsSelf>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiSupportContactsSelfQueryResult = NonNullable<Awaited<ReturnType<typeof getApiSupportContactsSelf>>>;
export type GetApiSupportContactsSelfQueryError = AxiosError<void>;

export const useGetApiSupportContactsSelf = <TData = Awaited<ReturnType<typeof getApiSupportContactsSelf>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiSupportContactsSelf>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiSupportContactsSelfQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiSupportContactsSelfSendActivation = (options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.put(`/api/Support/Contacts/Self/SendActivation`, undefined, options);
};

export const getPutApiSupportContactsSelfSendActivationMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiSupportContactsSelfSendActivation>>, TError, void, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiSupportContactsSelfSendActivation>>, TError, void, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiSupportContactsSelfSendActivation>>, void> = () => {
    return putApiSupportContactsSelfSendActivation(axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiSupportContactsSelfSendActivationMutationResult = NonNullable<Awaited<ReturnType<typeof putApiSupportContactsSelfSendActivation>>>;

export type PutApiSupportContactsSelfSendActivationMutationError = AxiosError<void>;

export const usePutApiSupportContactsSelfSendActivation = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiSupportContactsSelfSendActivation>>, TError, void, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiSupportContactsSelfSendActivation>>, TError, void, TContext> => {
  const mutationOptions = getPutApiSupportContactsSelfSendActivationMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupport = (
  brokerNerdsBarkerCoreModelsSupportSupportRequestBase: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support`, brokerNerdsBarkerCoreModelsSupportSupportRequestBase, options);
};

export const getPostApiSupportMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupport>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiSupport>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiSupport>>, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }> = (props) => {
    const { data } = props ?? {};

    return postApiSupport(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupport>>>;
export type PostApiSupportMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestBase;
export type PostApiSupportMutationError = AxiosError<void>;

export const usePostApiSupport = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupport>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiSupport>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext> => {
  const mutationOptions = getPostApiSupportMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportInventoryEvents = (
  brokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/Inventory/Events`, brokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent, options);
};

export const getPostApiSupportInventoryEventsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportInventoryEvents>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiSupportInventoryEvents>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiSupportInventoryEvents>>, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent }> = (
    props,
  ) => {
    const { data } = props ?? {};

    return postApiSupportInventoryEvents(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportInventoryEventsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportInventoryEvents>>>;
export type PostApiSupportInventoryEventsMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent;
export type PostApiSupportInventoryEventsMutationError = AxiosError<void>;

export const usePostApiSupportInventoryEvents = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportInventoryEvents>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiSupportInventoryEvents>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryEvent }, TContext> => {
  const mutationOptions = getPostApiSupportInventoryEventsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportInventoryListings = (
  brokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/Inventory/Listings`, brokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing, options);
};

export const getPostApiSupportInventoryListingsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportInventoryListings>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiSupportInventoryListings>>,
  TError,
  { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiSupportInventoryListings>>, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing }> = (
    props,
  ) => {
    const { data } = props ?? {};

    return postApiSupportInventoryListings(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportInventoryListingsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportInventoryListings>>>;
export type PostApiSupportInventoryListingsMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing;
export type PostApiSupportInventoryListingsMutationError = AxiosError<void>;

export const usePostApiSupportInventoryListings = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportInventoryListings>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postApiSupportInventoryListings>>,
  TError,
  { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestInventoryListing },
  TContext
> => {
  const mutationOptions = getPostApiSupportInventoryListingsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportMarketplacesMarketplaceIdEvents = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  brokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/Marketplaces/${marketplaceId}/Events`, brokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent, options);
};

export const getPostApiSupportMarketplacesMarketplaceIdEventsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdEvents>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdEvents>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdEvents>>,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent }
  > = (props) => {
    const { marketplaceId, data } = props ?? {};

    return postApiSupportMarketplacesMarketplaceIdEvents(marketplaceId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportMarketplacesMarketplaceIdEventsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdEvents>>>;
export type PostApiSupportMarketplacesMarketplaceIdEventsMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent;
export type PostApiSupportMarketplacesMarketplaceIdEventsMutationError = AxiosError<void>;

export const usePostApiSupportMarketplacesMarketplaceIdEvents = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdEvents>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdEvents>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceEvent },
  TContext
> => {
  const mutationOptions = getPostApiSupportMarketplacesMarketplaceIdEventsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportMarketplacesMarketplaceIdListings = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  brokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/Marketplaces/${marketplaceId}/Listings`, brokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing, options);
};

export const getPostApiSupportMarketplacesMarketplaceIdListingsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdListings>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdListings>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdListings>>,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing }
  > = (props) => {
    const { marketplaceId, data } = props ?? {};

    return postApiSupportMarketplacesMarketplaceIdListings(marketplaceId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportMarketplacesMarketplaceIdListingsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdListings>>>;
export type PostApiSupportMarketplacesMarketplaceIdListingsMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing;
export type PostApiSupportMarketplacesMarketplaceIdListingsMutationError = AxiosError<void>;

export const usePostApiSupportMarketplacesMarketplaceIdListings = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdListings>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postApiSupportMarketplacesMarketplaceIdListings>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsSupportSupportRequestMarketplaceListing },
  TContext
> => {
  const mutationOptions = getPostApiSupportMarketplacesMarketplaceIdListingsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportDTI = (
  brokerNerdsBarkerCoreModelsSupportSupportRequestBase: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/DTI`, brokerNerdsBarkerCoreModelsSupportSupportRequestBase, options);
};

export const getPostApiSupportDTIMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupportDTI>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiSupportDTI>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiSupportDTI>>, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }> = (props) => {
    const { data } = props ?? {};

    return postApiSupportDTI(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportDTIMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportDTI>>>;
export type PostApiSupportDTIMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestBase;
export type PostApiSupportDTIMutationError = AxiosError<void>;

export const usePostApiSupportDTI = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupportDTI>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiSupportDTI>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext> => {
  const mutationOptions = getPostApiSupportDTIMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportTktEdge = (
  brokerNerdsBarkerCoreModelsSupportSupportRequestBase: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/TktEdge`, brokerNerdsBarkerCoreModelsSupportSupportRequestBase, options);
};

export const getPostApiSupportTktEdgeMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupportTktEdge>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiSupportTktEdge>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiSupportTktEdge>>, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }> = (props) => {
    const { data } = props ?? {};

    return postApiSupportTktEdge(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportTktEdgeMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportTktEdge>>>;
export type PostApiSupportTktEdgeMutationBody = BrokerNerdsBarkerCoreModelsSupportSupportRequestBase;
export type PostApiSupportTktEdgeMutationError = AxiosError<void>;

export const usePostApiSupportTktEdge = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupportTktEdge>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiSupportTktEdge>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportSupportRequestBase }, TContext> => {
  const mutationOptions = getPostApiSupportTktEdgeMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiSupportErrors = (
  brokerNerdsBarkerCoreModelsSupportErrorReport: BrokerNerdsBarkerCoreModelsSupportErrorReport,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Support/Errors`, brokerNerdsBarkerCoreModelsSupportErrorReport, options);
};

export const getPostApiSupportErrorsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupportErrors>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportErrorReport }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiSupportErrors>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportErrorReport }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiSupportErrors>>, { data: BrokerNerdsBarkerCoreModelsSupportErrorReport }> = (props) => {
    const { data } = props ?? {};

    return postApiSupportErrors(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiSupportErrorsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiSupportErrors>>>;
export type PostApiSupportErrorsMutationBody = BrokerNerdsBarkerCoreModelsSupportErrorReport;
export type PostApiSupportErrorsMutationError = AxiosError<void>;

export const usePostApiSupportErrors = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiSupportErrors>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportErrorReport }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiSupportErrors>>, TError, { data: BrokerNerdsBarkerCoreModelsSupportErrorReport }, TContext> => {
  const mutationOptions = getPostApiSupportErrorsMutationOptions(options);

  return useMutation(mutationOptions);
};
